import React, { useCallback, useEffect, useState } from 'react'
import { Typography, Box, Button, Link, SxProps } from '@mui/material'
import { Lock } from '@mui/icons-material'
import Cookies from 'js-cookie'
import { useTranslation } from 'react-i18next'
import { CookieBannerDialog } from './CookieBannerDialog'
import { ConsentCookieName } from '../../types/cookies'

type ConsentListener = (p: Record<string, boolean>) => void

export function CookieBanner() {
  const { t } = useTranslation()
  const [isPreferencesOpen, setIsPreferencesOpen] = useState(false)

  const onSubmit = useCallback(
    (
      preferences = {
        analyticsConsentGranted: true,
        adConsentGranted: true,
      }
    ) => {
      setIsPreferencesOpen(false)

      const root = document.querySelector(':root') as HTMLElement
      root.style.setProperty('--banner-cookie-visibility', 'hidden')

      const listeners = (window.consentListeners as ConsentListener[]) ?? []
      listeners.forEach((cb) => cb(preferences))

      Cookies.set(ConsentCookieName, JSON.stringify(preferences), {
        expires: 365,
      })
    },
    []
  )

  // NOTE: disable btn's during hydration
  const [isReady, setIsReady] = useState(false)
  useEffect(() => {
    setIsReady(true)
  }, [setIsReady])

  return (
    <>
      <Box sx={styles.root}>
        <Box sx={styles.title_wrapper}>
          <Typography sx={styles.title} variant="h5">
            {t('cookiesBanner.cookieBannerTitle')}
          </Typography>
          <Lock fontSize="small" sx={{ ml: 1 }} />
        </Box>
        <Typography sx={styles.description}>
          {t('cookiesBanner.bannerDescription')}
          &nbsp;
          <Link
            href={`/${t('cookiesBanner.privacyUrl')}`}
            rel="noreferrer"
            target="_blank"
            sx={styles.link}
          >
            {t('cookiesBanner.bannerDescLinkText')}
          </Link>
          {t('cookiesBanner.bannerDescriptionCont')}
        </Typography>
        <Box sx={styles.footer}>
          <Button disabled={!isReady} onClick={() => onSubmit()}>
            {t('cookiesBanner.acceptAllText')}
          </Button>
          <Button
            disabled={!isReady}
            variant="outlined"
            onClick={() => setIsPreferencesOpen(true)}
          >
            {t('cookiesBanner.preferences')}
          </Button>
        </Box>
      </Box>
      <CookieBannerDialog
        isOpen={isPreferencesOpen}
        setIsOpen={setIsPreferencesOpen}
        onSubmit={onSubmit}
      />
    </>
  )
}

const styles = {
  root: {
    visibility: 'var(--banner-cookie-visibility, visible)',
    zIndex: 1100,
    display: 'flex',
    flexDirection: 'column',
    position: 'fixed',
    left: '50vw',
    transform: 'translateX(-50%)',
    bottom: '1.5rem',
    backgroundColor: '#fff',
    padding: '1.5rem',
    maxWidth: '80rem',
    width: '95%',
    borderRadius: '0.375rem',
    boxShadow: `
      0px 0px 1px rgba(15, 56, 113, 0.32),
      0px 2px 4px -1px rgba(15, 56, 113, 0.08),
      0px 8px 20px -4px rgba(15, 56, 113, 0.12)
    `,
  },
  title_wrapper: {
    display: 'flex',
  },
  title: {
    margin: 0,
    lineHeight: 1.431,
    letterSpacing: '0.1em',
  },
  description: {
    margin: 0,
    marginTop: 1,
    marginBottom: 1,
    letterSpacing: 0,
  },
  link: {
    color: 'secondary.main',
    '&:hover': {
      color: 'secondary.light',
    },
  },
  footer: {
    display: 'flex',
    height: 67,
    gap: 1,
  },
} satisfies Record<string, SxProps>
